// pages/Campaigns/Outlook/OutlookConnector.js
import React, { useState, useEffect, useCallback } from "react";
import {  useLocation, useNavigate, useParams } from 'react-router-dom';
import axiosInstance from "../../../utils/axiosInstance";
import { hasPermission } from "../../../utils/permissionUtils";
import ListOutlookEmails from "./ListOutlookEmails";
import {
  Box,
  Typography,
  Alert
} from '@mui/material';
export default function OutlookConnector() {
    const [isConnected, setIsConnected] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [isConnecting, setIsConnecting] = useState(false);
    const [error, setError] = useState('');
    const location = useLocation();
    const navigate = useNavigate();
    const [refreshTrigger, setRefreshTrigger] = useState(0);
    const { chatbotId : paramsChatbotId } = useParams();

    // Permission states
    const [canView, setCanView] = useState(false);
    const [canModify, setCanModify] = useState(false);
    const [canDelete, setCanDelete] = useState(false);

    const [checkingPermissions, setCheckingPermissions] = useState(true);


    // Check permissions
    useEffect(() => {
    const checkPermissions = async () => {
        setCheckingPermissions(true);
        try {
        const viewPerm = await hasPermission('campaigns', 'view');
        const modifyerm = await hasPermission('campaigns', 'add|edit');
        const deletePerm = await hasPermission('campaigns', 'delete');

        setCanView(viewPerm);
        setCanModify(modifyerm);
        setCanDelete(deletePerm);
        } catch (error) {
        console.error('Error checking permissions:', error);
        setCanView(false);
        setCanModify(false);
        setCanDelete(false);
        } finally {
        setCheckingPermissions(false);
        }
    };

    checkPermissions();
    }, []);
    const checkStatus = useCallback(async (showLoading = true) => {
        if (showLoading) {
            setIsLoading(true);
        }
        setError('');
        
        try {

            const params = paramsChatbotId ? { chatbot_id: paramsChatbotId } : {};
            const response = await axiosInstance.get(`/api/outlook/status`, { params });
            setIsConnected(response.data.connected);
        } catch (err) {
            console.error("Error checking Outlook status:", err);
            setError("Failed to check connection status. Please try again.");
            setIsConnected(false);
        } finally {
            setIsLoading(false);
        }
    }, []);

    useEffect(() => {
        document.title = 'Outlook Connector | KlonIT';

        const params = new URLSearchParams(location.search);

        if (params.has('connected')) {
            params.delete('connected');
            window.history.replaceState(null, '', `${location.pathname}?${params}`);
            checkStatus(false).then(() => setRefreshTrigger(p => p + 1));
            return;
        }

        if (params.has('error')) {
            setError(`Authentication failed: ${params.get('error')}`);
            params.delete('error');
            window.history.replaceState(null, '', `${location.pathname}?${params}`);
        }

        checkStatus();
    }, [location.search, checkStatus]);

    const handleConnect = async () => {
        setIsConnecting(true);
        setError('');
        
        try {
            const params = paramsChatbotId ? { chatbot_id: paramsChatbotId } : {};

            const response = await axiosInstance.get(`/api/outlook/auth/start`, { params } );
            if (response.data.authorization_url) {
                window.location.href = response.data.authorization_url;
            } else {
                setError("Failed to get authorization URL.");
            }
        } catch (err) {
            setError(err.response?.data?.message || "Could not connect to Outlook.");
        } finally {
            setIsConnecting(false);
        }
    };

    const handleDisconnect = async () => {
        setIsConnecting(true);
        setError('');
        
        try {
            const params = paramsChatbotId ? { chatbot_id: paramsChatbotId } : {};
            await axiosInstance.post(`/api/outlook/disconnect`, {}, { params });
            setIsConnected(false);
            setError('Successfully disconnected from Outlook.');
        } catch (err) {
            setError(err.response?.data?.message || "Failed to disconnect.");
        } finally {
            setIsConnecting(false);
        }
    };

      // Show loading state while checking permissions
    if (checkingPermissions) {
        return (
            <Box sx={{ display: "flex", justifyContent: "center", p: 5 }}>
            <Typography>Checking permissions...</Typography>
            </Box>
        );
    }

    // Show access denied if no view permission
    if (!canView) {
        return (
            <div>
            <Alert severity="error" sx={{ mt: 6 }}>
                <strong>Access Denied</strong>
                <br />
                You don't have permission to view Outlook settings. Please contact your administrator.
            </Alert>
            </div>
        );
    }
    

    return (
        <div>
            <main className="content">
                <div className="container-fluid p-0">
                    <div className="mb-3 d-flex justify-content-between align-items-center">
                        <h1 className="h3 d-inline align-middle"><strong>Outlook Auto-Responder</strong></h1>
                        {!isLoading && isConnected && canModify && (
                            <button className="btn btn-danger" onClick={handleDisconnect} disabled={isConnecting}>
                                {isConnecting ? 'Disconnecting...' : <><i className="fa fa-unlink me-1"></i> Disconnect Outlook</>}
                            </button>
                        )}
                    </div>

                    {isLoading && <p>Loading configuration...</p>}
                    
                    {error && (
                        <div className={`alert ${error.includes("Success") ? "alert-success" : "alert-danger"} mt-3`} role="alert">
                            {error}
                        </div>
                    )}

                    {!isLoading && !isConnected && canModify && (
                        <div className="card">
                            <div className="card-body text-center">
                                <p>Connect your Outlook account to automatically process and reply to emails.</p>
                                <button className="btn btn-lg btn-primary" onClick={handleConnect} disabled={isConnecting}>
                                    {isConnecting ? 'Connecting...' : <><i className="fa fa-windows me-2"></i> Connect to Outlook</>}
                                </button>
                            </div>
                        </div>
                    )}

                    
                    {!isLoading && isConnected  && (
                        <div className="row">
                            <div className="col-lg-12">
                                <div className="card">
                                    <div className="card-header">
                                        <h5 className="card-title mb-0">Incoming Emails</h5>
                                        <p className="mb-0">Emails are checked periodically. Use the buttons below to fetch new mail or manage settings.</p>
                                    </div>
                                    <div className="card-body">
                                        <ListOutlookEmails refreshTrigger={refreshTrigger} chatbotId={paramsChatbotId} permission={{'canView': canView, 'canModify': canModify, 'canDelete':canDelete}} />
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </main>
        </div>
    );
}